/**
 * Pure helpers for the `choice` core view (ported from the MEIS-13490
 * prototype's `agentChoiceSpec.ts`): validating a spec that came in over the
 * wire before rendering it, building the text sent as the user's answer,
 * and detecting which options a later user message chose (an older choice
 * form is shown read-only with those marked). What the assistant remembers
 * about a shown choice is the backend's `view.memory`, not computed here.
 */
import type { AgentViewSpec } from "./registry.js";

export type ChoiceOption = {
  label: string;
  description: string | null;
};

export type ChoiceViewSpec = {
  component: "choice";
  question: string;
  options: ChoiceOption[];
  multiSelect: boolean;
  allowOther: boolean;
};

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;
const MAX_LABEL_LENGTH = 80;
const MAX_DESCRIPTION_LENGTH = 160;

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Every problem with the spec, so a malformed view can be reported at once
 * instead of crashing on the first bad access. Empty means the spec is safe
 * to render. `view` is the loosely typed value that came in over the wire
 * (`AgentViewSpec`) — not yet trusted to be a `ChoiceViewSpec`.
 */
export function validateChoiceView(view: AgentViewSpec): string[] {
  const problems: string[] = [];

  if (typeof view.question !== "string" || view.question.trim() === "")
    problems.push("question: must not be empty");

  const options = view.options;
  if (
    !Array.isArray(options) ||
    options.length < MIN_OPTIONS ||
    options.length > MAX_OPTIONS
  ) {
    problems.push(
      `options: expected ${MIN_OPTIONS} to ${MAX_OPTIONS} entries, got ${
        Array.isArray(options) ? options.length : 0
      }`
    );
    return problems;
  }

  const seen = new Set<string>();
  options.forEach((rawOption, index) => {
    const position = index + 1;
    if (!isUnknownRecord(rawOption)) {
      problems.push(`option ${position}: not an object`);
      return;
    }
    const label = rawOption.label;
    if (typeof label !== "string" || label.trim() === "") {
      problems.push(`option ${position}: label must not be empty`);
      return;
    }
    if (label.length > MAX_LABEL_LENGTH)
      problems.push(
        `option ${position} ("${label}"): label too long (max. ${MAX_LABEL_LENGTH} characters)`
      );
    if (seen.has(label))
      problems.push(`option ${position} ("${label}"): duplicate label`);
    seen.add(label);
    const description = rawOption.description;
    if (
      typeof description === "string" &&
      description.length > MAX_DESCRIPTION_LENGTH
    )
      problems.push(
        `option ${position} ("${label}"): description too long (max. ${MAX_DESCRIPTION_LENGTH} characters)`
      );
  });

  return problems;
}

/** Whether the spec is safe to hand to the choice component as-is. */
export function isChoiceViewValid(view: AgentViewSpec): boolean {
  return validateChoiceView(view).length === 0;
}

/**
 * The exact text sent as the user's chat message once at least one option
 * (or the free-text "Other" field) is chosen: the picked labels in option
 * order, then the free text, comma-joined — plain text, as if typed.
 */
export function buildChoiceAnswerText(
  chosenLabels: readonly string[],
  otherText: string | null
): string {
  const trimmedOther = otherText?.trim() ?? "";
  const parts = [
    ...chosenLabels,
    ...(trimmedOther !== "" ? [trimmedOther] : []),
  ];
  return parts.join(", ");
}

/** Tolerates an answer sent in quotes (an earlier version of the form did that). */
function stripWrappingQuotes(text: string): string {
  const trimmed = text.trim();
  return trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')
    ? trimmed.slice(1, -1)
    : trimmed;
}

/**
 * The options an answer picks exactly (mirrors the backend's
 * `isStoredChoicePick`): the whole text is one label, or — for a multi-select
 * choice — labels in option order joined with ", ". Anything left over (an
 * "Other" text) is ignored here.
 */
function exactlyPickedLabels(view: ChoiceViewSpec, text: string): Set<string> {
  const labels = view.options.map(option => option.label);
  const whole = labels.find(label => label.trim() === text);
  if (whole !== undefined) return new Set([whole]);
  const picked = new Set<string>();
  if (!view.multiSelect) return picked;
  let rest = text;
  for (const label of labels) {
    const trimmed = label.trim();
    if (rest === trimmed) {
      picked.add(label);
      break;
    }
    if (rest.startsWith(`${trimmed}, `)) {
      picked.add(label);
      rest = rest.slice(trimmed.length + 2);
    }
  }
  return picked;
}

/**
 * Which of the view's options a later user message chose: for showing an
 * older choice form read-only, marking the options the following user
 * message picks exactly and, only when it picks none, the ones it merely
 * contains (a free-form answer). An exact pick never also marks an option
 * whose label it contains ("Bad" vs "Badezimmer"). Callers must validate
 * first — this trusts the shape.
 */
export function chosenOptionLabels(
  view: ChoiceViewSpec,
  answerText: string
): Set<string> {
  const unquoted = stripWrappingQuotes(answerText);
  const exact = exactlyPickedLabels(view, unquoted);
  if (exact.size > 0) return exact;
  const parts = unquoted
    .split(",")
    .map(part => part.trim())
    .filter(part => part !== "");
  const chosen = new Set<string>();
  for (const option of view.options) {
    if (parts.includes(option.label) || unquoted.includes(option.label))
      chosen.add(option.label);
  }
  return chosen;
}
